
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Terrace } from '@/types/terrace';
import { createMapMarker } from './MapMarker';

interface MapContainerProps {
  terraces: Terrace[];
  userLocation?: { lat: number; lng: number } | null;
  mapboxToken: string;
  onMapError: (error: string) => void;
  onMapClick?: () => void;
  interactive?: boolean; // New prop to control interactivity
}

const MapContainer: React.FC<MapContainerProps> = ({
  terraces,
  userLocation,
  mapboxToken,
  onMapError,
  onMapClick,
  interactive = true // Default to interactive
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!mapContainer.current || !mapboxToken) return;

    try {
      mapboxgl.accessToken = mapboxToken;

      // Calculate center and zoom based on terraces
      let center: [number, number] = [18.0686, 59.3293]; // Stockholm default
      let zoom = 12;

      if (terraces.length > 0) {
        if (terraces.length === 1) {
          // Single terrace - center on it
          center = [terraces[0].lng, terraces[0].lat];
          zoom = 15;
        } else {
          // Multiple terraces - calculate bounds
          const lats = terraces.map(t => t.lat);
          const lngs = terraces.map(t => t.lng);
          const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;
          const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
          center = [avgLng, avgLat];
        }
      }

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center,
        zoom,
        attributionControl: false,
        // Disable interactions if not interactive
        interactive: interactive,
        scrollZoom: interactive,
        boxZoom: interactive,
        dragRotate: interactive,
        dragPan: interactive,
        keyboard: interactive,
        doubleClickZoom: interactive,
        touchZoomRotate: interactive
      });

      // Only add navigation controls if interactive
      if (interactive) {
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      }

      // Add map click handler only if interactive and onMapClick is provided
      if (interactive && onMapClick) {
        map.current.on('click', (e) => {
          // Only trigger if not clicking on a marker
          if (!e.originalEvent.defaultPrevented) {
            onMapClick();
          }
        });
      }

      // Add markers for terraces
      terraces.forEach(terrace => {
        if (map.current) {
          const { createMarker } = createMapMarker({
            terrace,
            map: map.current,
            popupRef,
            navigate,
            interactive // Pass interactive state to markers
          });
          createMarker();
        }
      });

      // Add user location marker if available
      if (userLocation) {
        const userMarkerElement = document.createElement('div');
        userMarkerElement.className = 'w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-lg';
        
        new mapboxgl.Marker(userMarkerElement)
          .setLngLat([userLocation.lng, userLocation.lat])
          .addTo(map.current);
      }

    } catch (error) {
      console.error('Error initializing map:', error);
      onMapError(error instanceof Error ? error.message : 'Failed to initialize map');
    }

    return () => {
      map.current?.remove();
    };
  }, [terraces, userLocation, mapboxToken, onMapError, onMapClick, navigate, interactive]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainer} className="w-full h-full" />
    </div>
  );
};

export default MapContainer;
